<script setup lang="ts">
// Single tap = +1 score; double tap = −1 score.
// Uses manual tap-count debounce so the single-tap handler never fires
// on the first tap of a double-tap — circumventing racing between v-click
// and v-click.double firing in the same gesture sequence.

const store = useChallengeWheelStore()
const props = withDefaults(defineProps<{
  interactive?: boolean
}>(), {
  interactive: true,
})

const isInteractive = computed(() =>
  props.interactive && (store.phase === 'idle' || store.phase === 'playing' || store.phase === 'revealing'),
)

const tapTimers: (null | ReturnType<typeof setTimeout>)[] = []
const tapCounts: number[] = []

function handleTap(i: number) {
  if (!isInteractive.value) {
    return
  }
  tapCounts[i] = (tapCounts[i] ?? 0) + 1
  if (tapTimers[i]) {
    clearTimeout(tapTimers[i]!)
  }
  tapTimers[i] = setTimeout(() => {
    const count = tapCounts[i] ?? 0
    tapCounts[i] = 0
    tapTimers[i] = null
    if (count >= 2) {
      store.decrementScore(i)
    } else {
      store.incrementScore(i)
    }
  }, 280)
}
</script>

<template>
  <div class="flex h-full flex-col gap-4 justify-center">
    <div
      v-for="(team, i) in store.teams"
      :key="i"
      v-click="() => handleTap(i)"
      :data-vc-state-active="undefined"
      :style="{ backgroundColor: team.color }"
      :class="[
        'team-box relative flex flex-1 min-h-0 max-h-28 items-center justify-between gap-3 rounded-xl shadow-lg',
        'select-none touch-none',
        isInteractive ? 'cursor-pointer' : 'cursor-default',
        'data-vc-state-active:brightness-75',
      ]"
    >
      <span class="team-name min-w-0 flex-1 truncate font-bold text-white drop-shadow">{{ team.name }}</span>
      <span class="team-score font-black text-white drop-shadow tabular-nums">{{ team.score }}</span>
    </div>
  </div>
</template>

<style scoped>
/*
  Each team box is its own size-container (`container-type: size`)
  so its contents can size off both the box's height (cqh) and
  width (cqi).

  Text grows with the box but caps at whichever dimension would
  cause overflow first (`min(cqh, cqi)`):
    - height-bound on tall narrow boxes
    - width-bound on wide short boxes
  Inside the box, padding scales the same way — so the content
  always sits inset from the colored edge with a proportional
  cushion, never glued to the side. Outer hard caps prevent text
  from going absurd on huge displays. Spacing between boxes lives
  on the scoreboard column's `gap` and stays constant.
*/
.team-box {
  container-type: size;
  container-name: team;
  /* Padding scales slower than the text — small cqh/cqi % means
     padding hits its minimum well before the font has to shrink.
     Result: when the box gets small, the colored chrome tightens
     around the text first; only after that do the digits give way. */
  padding-inline: clamp(0.25rem, 3cqi, 1.25rem);
  padding-block: clamp(0.125rem, 3cqh, 0.75rem);
}
.team-name {
  font-size: clamp(0.85rem, min(45cqh, 12cqi), 2.5rem);
  line-height: 1.05;
}
.team-score {
  font-size: clamp(1rem, min(85cqh, 26cqi), 5rem);
  line-height: 1;
}
</style>
